import React, { createContext, useState, useEffect, useContext } from "react";

interface AuthContextProps {
  isAuthenticated: boolean;
  role: string | null;
  token: string | null;
  loading: boolean;
  login: (token: string, role: string, rememberMe?: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  role: null,
  token: null,
  loading: true,
  login: () => {},
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const rememberMe = localStorage.getItem("rememberMe") === "true";
    const storage = rememberMe ? localStorage : sessionStorage;

    const savedToken = storage.getItem("authToken");
    const savedRole = storage.getItem("role");

    if (savedToken && savedRole) {
      setToken(savedToken);
      setRole(savedRole);
    }

    // ✅ Done loading after checking storage
    setLoading(false);
  }, []);

  const login = (token: string, role: string, rememberMe: boolean = false) => {
    setToken(token);
    setRole(role);

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem("authToken", token);
    storage.setItem("role", role);
    localStorage.setItem("rememberMe", rememberMe ? "true" : "false");

    if (rememberMe) {
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("role");
    } else {
      localStorage.removeItem("authToken");
      localStorage.removeItem("role");
    }
  };

  const logout = () => {
    setToken(null);
    setRole(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("role");
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("role");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        role,
        token,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
